import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { AvailableLangs, TranslocoService } from '@ngneat/transloco';
import { Subject, take } from 'rxjs';
import { LanguagesService } from './languages.service';

@Component({
    selector: 'languages',
    templateUrl: './languages.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'languages',
})
export class LanguagesComponent implements OnInit, OnDestroy {
    /**
     * Objects
     */
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    availableLangs: AvailableLangs;
    activeLang: string;
    flagCodes: any;
    profileLang: string;
    flagIconUrl: string = 'assets/images/flags/EN.svg';

    /**
     * Constructor
     * @param _changeDetectorRef
     * @param _fuseNavigationService
     * @param _translocoService
     * @param _languagesService
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseNavigationService: FuseNavigationService,
        private _translocoService: TranslocoService,
        private _languagesService: LanguagesService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Get the available languages from transloco
        this.availableLangs = this._translocoService.getAvailableLangs();
        this._languagesService.lang$.subscribe({
            next: (response) => (this.profileLang = response),
        });

        // Subscribe to language changes
        this._translocoService.langChanges$.subscribe((activeLang) => {
            // Get the active lang
            this.activeLang = activeLang;

            // Update the navigation
            // if ( this.profileLang != null && this.profileLang != undefined && this.profileLang != this.activeLang ) {
            //     this._updateNavigation(this.profileLang);
            // } else {
            // }

            this._updateNavigation(activeLang);
            this._changeDetectorRef.markForCheck();
        });

        // Set the country iso codes for languages for flags
        this.flagCodes = {
            en: 'uk',
            el: 'el',
        };
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set the active lang
     * @param lang
     */
    setActiveLang(lang: string): void {
        // Set the active lang
        this._translocoService.setActiveLang(lang);
        this._languagesService.lang = lang;
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the navigation
     * @param lang
     * @private
     */
    private _updateNavigation(lang: string): void {
        // For the demonstration purposes, we will only update the Dashboard names
        // from the navigation but you can do a full swap and change the entire
        // navigation data.
        //
        // You can import the data from a file or request it from your backend,
        // it's up to you.

        // Get the component -> navigation data -> item
        const navComponent =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                'mainNavigation'
            );

        // Return if the navigation component does not exist
        if (!navComponent) {
            return null;
        }

        // Get the flat navigation data
        const navigation = navComponent.navigation;

        // Get the Dashboards item and update its title
        const dashboardsDashboardItem = this._fuseNavigationService.getItem(
            'dashboard',
            navigation
        );
        if (dashboardsDashboardItem) {
            this._translocoService
                .selectTranslate('Dashboards')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    dashboardsDashboardItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        // Get the Dashboards subtitle item and update its title
        const dashboardsSubDashboardItem = this._fuseNavigationService.getItem(
            'dashboard',
            navigation
        );
        if (dashboardsSubDashboardItem) {
            this._translocoService
                .selectTranslate('DashboardsSub')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    dashboardsSubDashboardItem.subtitle = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        // Get the Monitor dashboards item and update its title
        const monitorDashboardItem = this._fuseNavigationService.getItem(
            'monitor',
            navigation
        );
        if (monitorDashboardItem) {
            this._translocoService
                .selectTranslate('monitor.title')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    monitorDashboardItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        // Get the reports dashboards item and update its title
        const reportsItem = this._fuseNavigationService.getItem(
            'reports',
            navigation
        );
        if (reportsItem) {
            this._translocoService
                .selectTranslate('reports.title')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    reportsItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        // Get the Monitor subtitle dashboards item and update its title
        const reportsSubItem = this._fuseNavigationService.getItem(
            'reports',
            navigation
        );
        if (reportsSubItem) {
            this._translocoService
                .selectTranslate('reports.subtitle')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    reportsSubItem.subtitle = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        // Get the inventory dashboards item and update its title
        const inventoryItem = this._fuseNavigationService.getItem(
            'reports.inventory',
            navigation
        );
        if (inventoryItem) {
            this._translocoService
                .selectTranslate('inventory.title')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    inventoryItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        // Get the orders dashboards item and update its title
        const ordersItem = this._fuseNavigationService.getItem(
            'reports.orders',
            navigation
        );
        if (ordersItem) {
            this._translocoService
                .selectTranslate('nav-raw-data.orders')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    ordersItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        // Get the history dashboards item and update its title
        const historyItem = this._fuseNavigationService.getItem(
            'reports.history',
            navigation
        );
        if (historyItem) {
            this._translocoService
                .selectTranslate('history.title')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    historyItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        // Get the history dashboards item and update its title
        const comparativeInventoryMenuItem =
            this._fuseNavigationService.getItem(
                'reports.compInventory',
                navigation
            );
        if (comparativeInventoryMenuItem) {
            this._translocoService
                .selectTranslate('compInventory.title')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    comparativeInventoryMenuItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        // Get the history dashboards item and update its title
        const comparativeInventoryGenerate =
            this._fuseNavigationService.getItem('compInventoryGen', navigation);
        if (comparativeInventoryGenerate) {
            this._translocoService
                .selectTranslate('compInventory.generate')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    comparativeInventoryGenerate.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        // Get the history dashboards item and update its title
        const comparativeInventoryHistory = this._fuseNavigationService.getItem(
            'compInventoryHistory',
            navigation
        );
        if (comparativeInventoryHistory) {
            this._translocoService
                .selectTranslate('compInventory.history')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    comparativeInventoryHistory.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        const mainMenuItem = this._fuseNavigationService.getItem(
            'mainMenu',
            navigation
        );
        if (mainMenuItem) {
            this._translocoService
                .selectTranslate('main-menu.title')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    mainMenuItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        const mainSubMenuItem = this._fuseNavigationService.getItem(
            'mainMenu',
            navigation
        );
        if (mainSubMenuItem) {
            this._translocoService
                .selectTranslate('main-menu.subtitle')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    mainSubMenuItem.subtitle = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        const rawDataItem = this._fuseNavigationService.getItem(
            'raw.data',
            navigation
        );
        if (rawDataItem) {
            this._translocoService
                .selectTranslate('nav-raw-data.title')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    rawDataItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        const rawSubDataItem = this._fuseNavigationService.getItem(
            'raw.data',
            navigation
        );
        if (rawSubDataItem) {
            this._translocoService
                .selectTranslate('nav-raw-data.subtitle')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    rawSubDataItem.subtitle = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        const vehiclesItem = this._fuseNavigationService.getItem(
            'raw.data.vehicles',
            navigation
        );
        if (vehiclesItem) {
            this._translocoService
                .selectTranslate('nav-raw-data.vehicles')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    vehiclesItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        // const ordersItem = this._fuseNavigationService.getItem('raw.data.orders', navigation);
        // if ( ordersItem ) {
        //     this._translocoService.selectTranslate('nav-raw-data.orders').pipe(take(1))
        //         .subscribe((translation) => {

        //             // Set the title
        //             ordersItem.title = translation;

        //             // Refresh the navigation component
        //             navComponent.refresh();
        //         });
        // }

        const transactionsItem = this._fuseNavigationService.getItem(
            'raw.data.transactions',
            navigation
        );
        if (transactionsItem) {
            this._translocoService
                .selectTranslate('nav-raw-data.transactions')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    transactionsItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        const adminDashboardItem = this._fuseNavigationService.getItem(
            'admin',
            navigation
        );
        if (adminDashboardItem) {
            this._translocoService
                .selectTranslate('nav-admin.title')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    adminDashboardItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        const adminSubDashboardItem = this._fuseNavigationService.getItem(
            'admin',
            navigation
        );
        if (adminSubDashboardItem) {
            this._translocoService
                .selectTranslate('nav-admin.subtitle')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    adminDashboardItem.subtitle = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        const siteConfigItem = this._fuseNavigationService.getItem(
            'admin.siteConfig',
            navigation
        );
        if (siteConfigItem) {
            this._translocoService
                .selectTranslate('nav-admin.site-config')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    siteConfigItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        const integrationItem = this._fuseNavigationService.getItem(
            'integration',
            navigation
        );
        if (integrationItem) {
            this._translocoService
                .selectTranslate('nav-admin.integration.title')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    integrationItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        const integrationSubItem = this._fuseNavigationService.getItem(
            'integration',
            navigation
        );
        if (integrationSubItem) {
            this._translocoService
                .selectTranslate('nav-admin.integration.subtitle')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    integrationItem.subtitle = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        const adminUsersDashboardItem = this._fuseNavigationService.getItem(
            'admin.users',
            navigation
        );
        if (adminUsersDashboardItem) {
            this._translocoService
                .selectTranslate('nav-users')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    adminUsersDashboardItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }
        const adminFormDashboardItem = this._fuseNavigationService.getItem(
            'admin.form-inventory',
            navigation
        );
        if (adminFormDashboardItem) {
            this._translocoService
                .selectTranslate('car-check-form-inv.title')
                .pipe(take(1))
                .subscribe((translation) => {
                    // Set the title
                    adminFormDashboardItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }
    }
}
